import React, { memo, useEffect, useState } from 'react';
import { View, Platform } from 'react-native';
import Animated, { Extrapolation, interpolate, useSharedValue, useAnimatedStyle, } from 'react-native-reanimated';
import LinearGradient from 'utils/packages/linearGradient';
import { rem, ProgressBar, Spacer, ToolTip } from 'design-system';
import Text from 'design-system/Text';
import BlurAmountView from 'design-system/BlurAmountView';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { selectIsScrambled } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import useColorScheme from 'hooks/useColorScheme';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { isWeb } from '../../../constants';
import EditBudgetsCategoryModalContent from './EditBudgetsCategoryModalContent';
const SPACE = rem(16);
const strings = {
    zeroTotalBudgetModal: `Current total budget includes both the default amount and rolled-over from the last period. It’s currently £0.00 because a negative balance (overspend) was rolled over from last period.`,
};
const SetCategoryBudgetsAllocation = ({ currency, totalBudget, totalAllocated, scrollY, headerInfoHeight, baseLimit, rolledAmount, showZeroBudget = true, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const colorScheme = useColorScheme();
    const percentAllocated = (totalAllocated / totalBudget) * 100;
    const amountRemaining = totalBudget - totalAllocated;
    const scrambled = useAppSelector(selectIsScrambled);
    const isNegativeRollover = totalBudget <= 0;
    const value = useSharedValue(percentAllocated);
    const { paddingHorizontal } = useAppFrameDimensions(isWeb);
    const { isUnlockedForSpace } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const headerBackground = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: interpolate(scrollY.value, [0, headerInfoHeight, headerInfoHeight + 1], [headerInfoHeight, 0, 0]),
            },
        ],
    }));
    const [padding, setPadding] = useState(SPACE);
    const [borderRadius, setBorderRadius] = useState(0);
    function interpolateManually(value, inputRange, outputRange) {
        const [inMin, inMax] = inputRange;
        const [outMin, outMax] = outputRange;
        if (value <= inMin)
            return outMin;
        if (value >= inMax)
            return outMax;
        const ratio = (value - inMin) / (inMax - inMin);
        return outMin + ratio * (outMax - outMin);
    }
    const animatedContainer = useAnimatedStyle(() => {
        if (isWeb) {
            const clampedPadding = Math.max(SPACE * -2, interpolateManually(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [paddingHorizontal - SPACE, SPACE * -2]));
            const clampedRadus = Math.max(0, interpolateManually(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [SPACE, 0]));
            setPadding(clampedPadding);
            setBorderRadius(clampedRadus);
        }
        return {
            paddingHorizontal: interpolate(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [SPACE, (paddingHorizontal || 0) + SPACE], Extrapolation.CLAMP),
            shadowOpacity: colorScheme === 'dark'
                ? 0
                : interpolate(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [0, 1]),
            borderRadius: interpolate(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [SPACE, 0], Extrapolation.CLAMP),
            marginHorizontal: interpolate(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [SPACE + (paddingHorizontal || 0), 0], Extrapolation.CLAMP),
        };
    }, [scrollY, headerInfoHeight, paddingHorizontal, colorScheme]);
    useEffect(() => {
        value.value = percentAllocated;
    }, [percentAllocated, value]);
    const androidShadowOpacity = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY.value, [headerInfoHeight - 20, headerInfoHeight], [0, 1], Extrapolation.CLAMP),
    }));
    const progressBarColor = amountRemaining < 0
        ? [colors.elements.negative, colors.elements.negative]
        : [colors.elements.brand, colors.elements.brand];
    return (<Animated.View style={!isWeb ? [styles.card, headerBackground] : {}}>
      <View style={styles.cardContainer}>
        <Animated.View style={[
            styles.container,
            isWeb
                ? {
                    marginHorizontal: padding,
                    borderRadius,
                    marginTop: rem(-1),
                }
                : animatedContainer,
        ]}>
          <View style={[
            styles.titleRow,
            totalBudget > 0 ? { justifyContent: 'space-between' } : null,
        ]}>
            {totalBudget > 0 && (<Text Numbers-16>
                {Math.round(percentAllocated)}% <Text Text-16>set</Text>
              </Text>)}
            <BlurAmountView enabled={scrambled}>
              <Text Numbers-16>
                {formatAmount(totalAllocated, currency, 2)}/
                {formatAmount(totalBudget > 0 ? totalBudget : 0, currency, 2)}
              </Text>
            </BlurAmountView>

            {showZeroBudget && totalBudget <= 0 ? (<>
                <Spacer.W8 />
                <ToolTip colorKey="secondary" title={'Why total budget is 0?'} subtitle={!isUnlockedForSpace ? (strings.zeroTotalBudgetModal) : (<EditBudgetsCategoryModalContent category={{
                    totalLimit: baseLimit + rolledAmount,
                    baseLimit,
                    rollingAccumulatedLimit: rolledAmount,
                    shouldRollover: true,
                    currency,
                }} showSubDescription={false} explainZeroBudget showEditingText={false} isToolTip/>)} small offset={0}/>
              </>) : null}
          </View>

          {isNegativeRollover ? null : (<>
              <Spacer.H8 />
              <ProgressBar position={percentAllocated || 0} animateProgress={false} barBackgroundColor={colors.background.dark} colors={progressBarColor}/>
              <Spacer.H8 />
            </>)}

          <LeftToAllocate currency={currency} scrambled={scrambled} amountRemaining={amountRemaining} isNegativeRollover={isNegativeRollover} showZeroBudget/>
        </Animated.View>
      </View>
      {Platform.OS === 'android' && (<Animated.View style={androidShadowOpacity}>
          <LinearGradient pointerEvents="none" style={[styles.androidShadow]} colors={[
                colors.shadows.androidShadowGradientStart,
                colors.shadows.androidShadowGradientEnd,
            ]}/>
        </Animated.View>)}
    </Animated.View>);
};
const LeftToAllocate = ({ amountRemaining, currency, scrambled, isNegativeRollover, showZeroBudget, }) => {
    if (isNegativeRollover && showZeroBudget) {
        return (<Text TextThin-14 Gray>{`You dont have a total budget to allocate`}</Text>);
    }
    return amountRemaining < 0 ? (<BlurAmountView enabled={scrambled}>
      <Text TextThin-14 Red>
        Over budget by{' '}
        <Text NumbersThin-14 Red>
          {formatAmount(Math.abs(amountRemaining), currency, 2)}
        </Text>
      </Text>
    </BlurAmountView>) : (<BlurAmountView enabled={scrambled}>
      <Text TextThin-14 Gray>
        <Text NumbersThin-14 Gray>
          {formatAmount(amountRemaining, currency, 2)}
        </Text>{' '}
        left to allocate
      </Text>
    </BlurAmountView>);
};
const styleSet = createStyleSheets((colors) => ({
    card: {
        position: 'absolute',
        left: 0,
        right: 0,
        zIndex: 1,
        height: rem(95),
    },
    container: {
        padding: rem(16),
        backgroundColor: colors.cards.onDark,
        ...colors.shadows.extendedHeader,
        ...(isWeb && {
            borderRadius: rem(16),
        }),
    },
    cardContainer: {
        ...(isWeb && {
            paddingVertical: rem(1),
            marginHorizontal: rem(32),
            backgroundColor: colors.background.dark,
        }),
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    androidShadow: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 10,
    },
}));
export const allocationCardHeight = rem(95);
export default memo(SetCategoryBudgetsAllocation);
